import { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ArrowRight, Check, ChevronLeft, Loader2, Pencil, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { haptics } from '@/lib/haptics';
import { useIsMobile } from '@/hooks/use-mobile';
import { MobileFullscreenPage } from '@/components/layout/MobileFullscreenPage';
import type { UserItem } from '@/types/userItem';
import { useBulkListPriceQueue, type BulkListPriceDecision } from './useBulkListPriceQueue';
import { PRICE_INPUT_PROPS } from '@/lib/priceInputProps';

function getPrimaryImage(item: UserItem): string | null {
  return item.images?.[0]?.image_url ?? item.stock_image_url ?? null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Unpriced items to walk through. */
  items: UserItem[];
  /** How many additional selected items already have prices (context for the done screen). */
  alreadyPricedCount: number;
  isProcessing: boolean;
  onComplete: (decisions: Map<string, BulkListPriceDecision>) => void | Promise<void>;
  /** Optional: fired the instant a decision is made so callers can autosave per-item. */
  onDecision?: (item: UserItem, decision: BulkListPriceDecision) => void | Promise<void>;
  /** Optional: fired when the user edits an item's title inline. */
  onTitleChange?: (item: UserItem, title: string) => void | Promise<void>;
}

function formatCents(cents: number | null | undefined) {
  if (!cents || cents <= 0) return null;
  return (cents / 100).toFixed(2);
}

export function BulkListPriceQueueDialog({
  open,
  onOpenChange,
  items,
  alreadyPricedCount,
  isProcessing,
  onComplete,
  onDecision,
  onTitleChange,
}: Props) {
  const isMobile = useIsMobile();
  const queue = useBulkListPriceQueue(items, open);
  const [draft, setDraft] = useState('');
  const [titleOverrides, setTitleOverrides] = useState<Record<string, string>>({});
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const titleInputRef = useRef<HTMLInputElement>(null);
  // Sticky across items — set once, keeps for the rest of the queue.
  const [acceptsOffers, setAcceptsOffers] = useState(true);
  const [offersOnlyActive, setOffersOnlyActive] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset draft on item change; prefill with market estimate when we have it.
  useEffect(() => {
    if (!queue.current) {
      setDraft('');
      setOffersOnlyActive(false);
      setEditingTitle(false);
      return;
    }
    const marketPrefill = formatCents(queue.current.collectr_market_price_cents);
    setDraft(marketPrefill ?? '');
    setOffersOnlyActive(false);
    setEditingTitle(false);
    // Focus is optional — mobile users may not want the keyboard immediately.
    if (!isMobile) {
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [queue.current?.id, isMobile]);

  useEffect(() => {
    if (open) setTitleOverrides({});
  }, [open]);

  const commitTitle = () => {
    if (!queue.current) return;
    const next = titleDraft.trim();
    if (!next || next === (titleOverrides[queue.current.id] ?? queue.current.title)) {
      setEditingTitle(false);
      return;
    }
    setTitleOverrides((prev) => ({ ...prev, [queue.current!.id]: next }));
    onTitleChange?.(queue.current, next);
    setEditingTitle(false);
  };

  const beginEditTitle = () => {
    if (!queue.current) return;
    setTitleDraft(titleOverrides[queue.current.id] ?? queue.current.title);
    setEditingTitle(true);
    requestAnimationFrame(() => titleInputRef.current?.focus());
  };

  const parsedPrice = draft ? parseFloat(draft) : NaN;
  const canSubmitPrice = Number.isFinite(parsedPrice) && parsedPrice > 0;
  const canSubmit = offersOnlyActive || canSubmitPrice;

  const submitPrice = () => {
    if (!queue.current) return;
    if (offersOnlyActive) {
      haptics.tap();
      onDecision?.(queue.current, { kind: 'offers' });
      queue.offersOnly();
      return;
    }
    if (!canSubmitPrice) return;
    haptics.tap();
    const priceCents = Math.round(parsedPrice * 100);
    onDecision?.(queue.current, { kind: 'price', priceCents, acceptsOffers });
    queue.price(priceCents, acceptsOffers);
  };

  const toggleOffersOnly = () => {
    haptics.tap();
    setOffersOnlyActive((v) => !v);
  };

  const handleSkip = () => {
    if (!queue.current) return;
    haptics.tap();
    onDecision?.(queue.current, { kind: 'skip' });
    queue.skip();
  };

  const totalToList = queue.summary.priced + queue.summary.offers + alreadyPricedCount;
  const primary = queue.current;
  const primaryImage = primary ? getPrimaryImage(primary) : null;

  // If there's only one item to list, skip the review screen and list it immediately.
  const shouldAutoComplete = queue.isDone && totalToList === 1;
  const autoCompletedRef = useRef(false);

  useEffect(() => {
    if (open) autoCompletedRef.current = false;
  }, [open]);

  useEffect(() => {
    if (shouldAutoComplete && !isProcessing && !autoCompletedRef.current) {
      autoCompletedRef.current = true;
      onComplete(queue.decisions);
    }
  }, [shouldAutoComplete, isProcessing, onComplete, queue.decisions]);

  // Desktop-only keyboard shortcuts:
  //   Enter or L → submit price / list
  //   O → toggle offers-only
  //   S or → → skip
  //   ← → back
  //   E → begin editing title inline
  useEffect(() => {
    if (!open || isMobile || queue.isDone) return;
    const handler = (e: KeyboardEvent) => {
      if (e.repeat || e.metaKey || e.ctrlKey || e.altKey) return;
      const target = e.target as HTMLElement | null;
      const inInput = target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA';
      const key = e.key;
      if (key === 'o' || key === 'O') {
        if (inInput) return;
        e.preventDefault();
        toggleOffersOnly();
      } else if (key === 's' || key === 'S') {
        if (inInput) return;
        e.preventDefault();
        handleSkip();
      } else if ((key === 'l' || key === 'L') && !inInput) {
        e.preventDefault();
        submitPrice();
      } else if ((key === 'e' || key === 'E') && !inInput) {
        e.preventDefault();
        beginEditTitle();
      } else if (key === 'ArrowLeft' && !inInput) {
        e.preventDefault();
        queue.back();
      } else if (key === 'ArrowRight' && !inInput) {
        e.preventDefault();
        handleSkip();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, isMobile, queue, toggleOffersOnly, handleSkip, submitPrice, beginEditTitle]);

  // --- Shared sub-blocks (used by both mobile page + desktop dialog) --------

  const progressBlock = (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span className="font-medium">
          {queue.isDone ? 'All done' : `${Math.min(queue.index + 1, queue.total)} of ${queue.total}`}
        </span>
        <span className="tabular-nums">
          <span className="text-active-gold">{queue.summary.priced}</span> priced
          {queue.summary.offers > 0 && <> · {queue.summary.offers} offers</>}
          {queue.summary.skipped > 0 && <> · {queue.summary.skipped} skipped</>}
        </span>
      </div>
      <div className="h-1 rounded-full bg-white/[0.06] overflow-hidden">
        <div
          className="h-full bg-active-gold transition-all duration-300"
          style={{ width: `${queue.total === 0 ? 0 : (queue.index / queue.total) * 100}%` }}
        />
      </div>
    </div>
  );

  const doneBlock = (
    <div className="flex flex-col items-center justify-center gap-3 py-8 text-center">
      <div className="w-12 h-12 rounded-full bg-success/15 border border-success/30 flex items-center justify-center">
        <Check className="w-6 h-6 text-success" />
      </div>
      <div className="text-[15px] font-semibold text-foreground">Review complete</div>
      <div className="text-sm text-muted-foreground max-w-xs">
        {totalToList} item{totalToList === 1 ? '' : 's'} ready to list
        {queue.summary.skipped > 0 && <> · {queue.summary.skipped} left unlisted</>}
        .
      </div>
    </div>
  );

  const autoCompleteLoadingBlock = (
    <div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
      <Loader2 className="w-8 h-8 text-active-gold animate-spin" />
      <div className="text-[15px] font-semibold text-foreground">
        Listing {totalToList} item{totalToList === 1 ? '' : 's'}…
      </div>
    </div>
  );

  const itemCard = primary ? (
    <div className="flex items-center gap-3 rounded-xl border border-white/[0.06] bg-white/[0.04] p-3 h-[76px]">
      <div className="w-14 h-14 rounded-lg overflow-hidden bg-white/[0.08] shrink-0 flex items-center justify-center">
        {primaryImage ? (
          <img
            src={primaryImage}
            alt=""
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover"
          />
        ) : (
          <span className="text-[10px] text-muted-foreground">no photo</span>
        )}
      </div>
      <div className="min-w-0 flex-1">
        {editingTitle ? (
          <div className="flex items-center gap-1">
            <Input
              ref={titleInputRef}
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  commitTitle();
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  setEditingTitle(false);
                }
              }}
              onBlur={commitTitle}
              className="h-7 text-[14px] font-semibold px-2 py-0 bg-white/[0.06] border-white/[0.12]"
            />
            <button
              type="button"
              onClick={() => setEditingTitle(false)}
              className="shrink-0 p-1 text-muted-foreground hover:text-foreground"
              aria-label="Cancel"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={beginEditTitle}
            className="group flex items-center gap-1.5 min-w-0 max-w-full text-left"
            aria-label="Edit title"
          >
            <span className="text-[14px] font-semibold text-foreground leading-tight truncate">
              {titleOverrides[primary.id] ?? primary.title}
            </span>
            <Pencil className="w-3 h-3 text-muted-foreground/60 group-hover:text-active-gold shrink-0 transition-colors" />
          </button>
        )}
        <div className="text-[11px] text-muted-foreground mt-0.5 truncate">
          {[primary.game, primary.set_name, primary.card_number ? `#${primary.card_number}` : null]
            .filter(Boolean)
            .join(' · ')}
        </div>
        {primary.collectr_market_price_cents ? (
          <div className="text-[11px] text-muted-foreground mt-0.5">
            Market est. ${formatCents(primary.collectr_market_price_cents)}
          </div>
        ) : null}
      </div>
    </div>
  ) : null;

  const priceInput = (
    <div
      onClick={() => setOffersOnlyActive(false)}
      className={cn(
        'flex items-center gap-1 rounded-xl border pl-4 pr-2 transition-colors',
        isMobile ? 'h-14' : 'h-12',
        offersOnlyActive
          ? 'bg-white/[0.02] border-white/[0.03] text-muted-foreground/50 pointer-events-none'
          : 'bg-white/[0.04] border-white/[0.06] focus-within:border-white/[0.12]',
      )}
    >
      <span className={cn('text-muted-foreground', isMobile ? 'text-[18px]' : 'text-[16px]')}>$</span>
      <Input
        ref={inputRef}
        type="text"
        inputMode="decimal"
        pattern="[0-9]*\.?[0-9]*"
        {...PRICE_INPUT_PROPS}
        placeholder="0.00"
        value={draft}
        disabled={offersOnlyActive}
        onChange={(e) => setDraft(e.target.value.replace(/[^0-9.]/g, ''))}

        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            submitPrice();
          }
        }}
        className={cn(
          'h-full flex-1 !bg-transparent !border-0 shadow-none text-foreground focus-visible:ring-0 focus-visible:ring-offset-0 px-1 disabled:opacity-40',
          isMobile ? 'text-[18px]' : 'text-[16px]',
        )}
      />
    </div>
  );

  const acceptsOffersChip = (
    <button
      type="button"
      disabled={offersOnlyActive}
      onClick={() => setAcceptsOffers((v) => !v)}
      className={cn(
        'w-full flex items-center gap-3 rounded-xl border transition-colors text-left',
        isMobile ? 'h-14 px-4' : 'h-12 px-4',
        offersOnlyActive && 'opacity-40 pointer-events-none',
        !offersOnlyActive &&
          (acceptsOffers
            ? 'bg-active-gold/10 border-active-gold/40 text-active-gold'
            : 'bg-white/[0.04] border-white/[0.06] text-muted-foreground hover:text-foreground hover:bg-white/[0.06]'),
      )}
    >
      <span
        className={cn(
          'w-4 h-4 rounded-full border flex items-center justify-center transition-colors shrink-0',
          acceptsOffers ? 'bg-active-gold border-active-gold' : 'border-muted-foreground/50',
        )}
      >
        {acceptsOffers && <Check className="w-2.5 h-2.5 text-background" strokeWidth={3} />}
      </span>
      <span className={cn('font-medium', isMobile ? 'text-[16px]' : 'text-sm')}>Also accept offers</span>
    </button>
  );

  const offersOnlyField = (
    <button
      type="button"
      onClick={toggleOffersOnly}
      className={cn(
        'w-full flex items-center gap-3 rounded-xl border transition-colors text-left',
        isMobile ? 'h-14 px-4' : 'h-12 px-4',
        offersOnlyActive
          ? 'bg-active-gold/10 border-active-gold/40 text-active-gold'
          : 'bg-white/[0.04] border-white/[0.06] text-muted-foreground hover:text-foreground hover:bg-white/[0.06]',
      )}
    >
      <span
        className={cn(
          'w-4 h-4 rounded-full border flex items-center justify-center transition-colors shrink-0',
          offersOnlyActive ? 'bg-active-gold border-active-gold' : 'border-muted-foreground/50',
        )}
      >
        {offersOnlyActive && <Check className="w-2.5 h-2.5 text-background" strokeWidth={3} />}
      </span>
      <span className={cn('font-medium', isMobile ? 'text-[16px]' : 'text-sm')}>Offers only</span>
    </button>
  );

  const secondaryActions = (
    <div className="flex items-center justify-between gap-2 pt-1">
      <Button
        variant="ghost"
        onClick={() => queue.back()}
        disabled={queue.index === 0}
        className={cn(
          'rounded-full text-muted-foreground hover:text-foreground disabled:opacity-30',
          isMobile && 'h-11 px-4',
        )}
      >
        <ChevronLeft className="w-4 h-4 mr-1" />
        Back
      </Button>
      <Button
        variant="ghost"
        onClick={handleSkip}
        className={cn(
          'rounded-full text-muted-foreground hover:text-foreground',
          isMobile && 'h-11 px-4',
        )}
      >
        Skip
      </Button>
    </div>
  );

  const desktopSetPriceButton = (
    <Button onClick={submitPrice} disabled={!canSubmit} className="w-full h-11 rounded-full">
      {offersOnlyActive ? 'Set offers only' : 'Set price'}
      <ArrowRight className="w-4 h-4 ml-2" />
    </Button>
  );

  const listCtaButton = (
    <Button
      onClick={() => onComplete(queue.decisions)}
      disabled={isProcessing || totalToList === 0}
      className="w-full h-12 rounded-full text-[15px] font-semibold"
    >
      {isProcessing ? (
        <>
          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          Listing…
        </>
      ) : (
        `List ${totalToList} item${totalToList === 1 ? '' : 's'}`
      )}
    </Button>
  );

  // --- Mobile: full-screen page with sticky footer -------------------------

  if (isMobile) {
    const mobileFooter = shouldAutoComplete ? null : queue.isDone ? (
      <div className="flex flex-col gap-2">
        {listCtaButton}
        <Button
          variant="ghost"
          onClick={() => onOpenChange(false)}
          disabled={isProcessing}
          className="w-full h-10 rounded-full text-muted-foreground"
        >
          Cancel
        </Button>
      </div>
    ) : (
      <Button
        onClick={submitPrice}
        disabled={!canSubmit}
        className="w-full h-14 rounded-full text-[16px] font-semibold"
      >
        {offersOnlyActive ? 'Set offers only' : 'Set price'}
        <ArrowRight className="w-5 h-5 ml-2" />
      </Button>
    );

    return (
      <MobileFullscreenPage
        open={open}
        onClose={() => onOpenChange(false)}
        title="Price items to list"
        surface="solid"
        footer={mobileFooter}
      >
        <div className="flex flex-col gap-5 p-4">
          {progressBlock}
          {queue.isDone ? (
            shouldAutoComplete ? (
              autoCompleteLoadingBlock
            ) : (
              doneBlock
            )
          ) : primary ? (
            <>
              {itemCard}
              <div className="space-y-3">
                {priceInput}
                {acceptsOffersChip}
                {offersOnlyField}
              </div>
              {secondaryActions}
            </>
          ) : null}
        </div>
      </MobileFullscreenPage>
    );
  }

  // --- Desktop: keep the centered dialog -----------------------------------

  const desktopBody = (
    <div className="flex flex-col gap-4">
      {progressBlock}
      {queue.isDone ? (
        shouldAutoComplete ? (
          autoCompleteLoadingBlock
        ) : (
          <>
            {doneBlock}
            <div className="flex items-center justify-center gap-2">
              <Button
                variant="ghost"
                onClick={() => onOpenChange(false)}
                disabled={isProcessing}
                className="rounded-full"
              >
                Cancel
              </Button>
              <Button
                onClick={() => onComplete(queue.decisions)}
                disabled={isProcessing || totalToList === 0}
                className="rounded-full px-6"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Listing…
                  </>
                ) : (
                  `List ${totalToList} item${totalToList === 1 ? '' : 's'}`
                )}
              </Button>
            </div>
          </>
        )
      ) : primary ? (
        <>
          {itemCard}
          <div className="space-y-2">
            {priceInput}
            {acceptsOffersChip}
            {offersOnlyField}
            {desktopSetPriceButton}
          </div>
          {secondaryActions}
          <div className="text-[11px] text-muted-foreground/70 text-center pt-1 tracking-wide leading-relaxed">
            <kbd className="px-1 rounded bg-white/[0.06] text-muted-foreground">Enter</kbd> / <kbd className="px-1 rounded bg-white/[0.06] text-muted-foreground">L</kbd> set price ·{' '}
            <kbd className="px-1 rounded bg-white/[0.06] text-muted-foreground">O</kbd> offers only ·{' '}
            <kbd className="px-1 rounded bg-white/[0.06] text-muted-foreground">E</kbd> edit title ·{' '}
            <kbd className="px-1 rounded bg-white/[0.06] text-muted-foreground">S</kbd> skip ·{' '}
            <kbd className="px-1 rounded bg-white/[0.06] text-muted-foreground">←</kbd> back
          </div>
        </>
      ) : null}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-lg w-[32rem] max-w-[calc(100vw-2rem)] max-h-[90dvh] overflow-y-auto"
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Price items to list</DialogTitle>
          <DialogDescription className="sr-only">
            Walk through unpriced items one at a time — set a price, accept offers, or skip.
          </DialogDescription>
        </DialogHeader>
        {desktopBody}
      </DialogContent>
    </Dialog>
  );
}
